import React, { createContext, useContext, useState, useEffect } from 'react';

interface LanguageContextType {
  language: 'ar' | 'en';
  setLanguage: (lang: 'ar' | 'en') => void;
  t: (key: string) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation dictionary
const translations = {
  ar: {
    // Navigation
    'nav.home': 'الرئيسية',
    'nav.services': 'الخدمات',
    'nav.process': 'العملية',
    'nav.contact': 'اتصل بنا',
    
    // Common
    'common.loading': 'جاري التحميل...',
    'common.save': 'حفظ',
    'common.cancel': 'إلغاء',
    'common.edit': 'تحرير',
    'common.delete': 'حذف',
    'common.preview': 'معاينة',
    'common.publish': 'نشر',
    'common.draft': 'مسودة',
    'common.active': 'نشط',
    'common.settings': 'الإعدادات',
    'common.search': 'بحث...',
    'common.close': 'إغلاق',
    'common.open': 'فتح',
    'common.back': 'رجوع',
    'common.next': 'التالي',
    'common.previous': 'السابق',
    'common.submit': 'إرسال',
    'common.reset': 'إعادة تعيين',
    
    // Hero Section
    'hero.title': 'ننمو بالبيانات، ونُبدع بالنتائج',
    'hero.subtitle': 'شريك نمو قائم على البيانات يقود العلامات التجارية باستخدام تسويق الأداء',
    'hero.cta.primary': 'تحليل مجاني',
    'hero.cta.secondary': 'شاهد أعمالنا',
    'hero.badge': 'وكالة تسويق نمو وأداء',
    
    // Services
    'services.title': 'خدماتنا المتخصصة',
    'services.subtitle': 'حلول تسويقية شاملة لنمو علامتك التجارية',
    'services.performance.title': 'تسويق الأداء',
    'services.performance.desc': 'حملات إعلانية مدفوعة بالبيانات عبر جميع المنصات الرقمية',
    'services.growth.title': 'تسويق النمو',
    'services.growth.desc': 'استراتيجيات نمو شاملة لتوسيع قاعدة عملائك',
    'services.ai-video.title': 'إنتاج فيديو بالذكاء الاصطناعي',
    'services.ai-video.desc': 'محتوى فيديو إبداعي باستخدام أحدث تقنيات الذكاء الاصطناعي',
    
    // Stats
    'stats.visitors': 'زوار اليوم',
    'stats.conversion': 'معدل التحويل',
    'stats.speed': 'سرعة الموقع',
    'stats.seo': 'نقاط SEO',
    'stats.clients': 'عميل راضٍ',
    'stats.views': 'مشاهدة شهرياً',
    'stats.support': 'دعم متواصل',
    'stats.roas': 'متوسط زيادة ROAS',
    
    // Why Us
    'why-us.title': 'لماذا تختار ريشة؟',
    'why-us.subtitle': 'نحن لا نقدم خدمات فقط، بل نبني شراكات نجاح',
    'why-us.expert-team': 'فريق خبراء متخصص',
    'why-us.measurable-results': 'نتائج قابلة للقياس',
    'why-us.advanced-tech': 'تقنيات متطورة',
    'why-us.support': 'دعم على مدار الساعة',
    'why-us.testimonial': 'ريشة غيرت مسار أعمالنا بالكامل. النتائج تتحدث عن نفسها.',
    'why-us.rating': 'من العملاء',
    
    // Contact
    'contact.title': 'تواصل معنا',
    'contact.subtitle': 'نحن هنا لمساعدتك في تحقيق أهدافك',
    'contact.phone': 'الهاتف',
    'contact.email': 'البريد الإلكتروني',
    'contact.address': 'العنوان',
    'contact.form.name': 'الاسم',
    'contact.form.email': 'البريد الإلكتروني',
    'contact.form.message': 'الرسالة',
    'contact.form.send': 'إرسال الرسالة',
    
    // Footer
    'footer.description': 'شريك نمو قائم على البيانات',
    'footer.contact-us': 'تواصل معنا',
    'footer.follow-us': 'تابعنا',
    'footer.rights': 'جميع الحقوق محفوظة',
    
    // Dashboard
    'dashboard.welcome': 'مرحباً بك في لوحة التحكم المتقدمة',
    'dashboard.welcome.subtitle': 'إدارة موقعك بسهولة وفعالية مع أدوات متقدمة وواجهة سهلة الاستخدام',
    'dashboard.quick-setup': 'إعداد سريع',
    'dashboard.guided-tour': 'جولة إرشادية',
    'dashboard.quick-actions': 'إجراءات سريعة',
    'dashboard.recent-activity': 'النشاط الأخير',
    'dashboard.view-all': 'عرض الكل',
    'dashboard.processing': 'جاري المعالجة...',
    
    // Quick Actions
    'actions.preview-website': 'معاينة الموقع',
    'actions.publish-changes': 'نشر التغييرات',
    'actions.create-backup': 'نسخ احتياطي',
    'actions.download-theme': 'تحميل القالب',
    'actions.analytics-report': 'تقرير التحليلات',
    'actions.optimize-performance': 'تحسين الأداء',
    
    // Projects
    'projects.title': 'إدارة المشاريع',
    'projects.new-project': 'مشروع جديد',
    'projects.status': 'الحالة',
    'projects.last-modified': 'آخر تعديل',
    'projects.views': 'المشاهدات',
    'projects.published': 'منشور',
    
    // Tabs
    'tabs.dashboard': 'لوحة التحكم',
    'tabs.projects': 'المشاريع',
    'tabs.visual-editor': 'المحرر المرئي',
    'tabs.content-manager': 'إدارة المحتوى',
    'tabs.design-system': 'نظام التصميم',
    'tabs.analytics': 'التحليلات',
    'tabs.media-library': 'المكتبة',
    'tabs.settings': 'الإعدادات',
    
    // Coming Soon
    'coming-soon.title': 'قريباً',
    'coming-soon.subtitle': 'هذا القسم قيد التطوير وسيكون متاحاً قريباً',
    
    // Theme Download
    'download.title': 'تحميل قالب ووردبريس',
    'download.subtitle': 'حزمة قالب ووردبريس كاملة',
    'download.features': 'المحرر المرئي • الدعم متعدد اللغات • لوحة التحكم • متوافق مع OT',
    'download.ready': 'القالب جاهز للتحميل!',
    'download.complete': 'حزمة قالب ووردبريس كاملة جاهزة',
    'download.button': 'تحميل معلومات القالب والتعليمات',
    'download.location': 'موقع ملفات القالب',
    'download.instructions': 'تعليمات التثبيت',
    'download.requirements': 'المتطلبات',
    
    // Success Messages
    'success.saved': 'تم الحفظ بنجاح!',
    'success.published': 'تم النشر بنجاح!',
    'success.deleted': 'تم الحذف بنجاح!',
    'success.updated': 'تم التحديث بنجاح!',
    'success.created': 'تم الإنشاء بنجاح!',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.process': 'Process',
    'nav.contact': 'Contact',
    
    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.preview': 'Preview',
    'common.publish': 'Publish',
    'common.draft': 'Draft',
    'common.active': 'Active',
    'common.settings': 'Settings',
    'common.search': 'Search...',
    'common.close': 'Close',
    'common.open': 'Open',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.submit': 'Submit',
    'common.reset': 'Reset',
    
    // Hero Section
    'hero.title': 'We Grow with Data, Create with Results',
    'hero.subtitle': 'Data-driven growth partner that scales brands using performance marketing',
    'hero.cta.primary': 'Free Audit',
    'hero.cta.secondary': 'View Our Work',
    'hero.badge': 'Growth & Performance Marketing Agency',
    
    // Services
    'services.title': 'Our Specialized Services',
    'services.subtitle': 'Comprehensive marketing solutions for your brand growth',
    'services.performance.title': 'Performance Marketing',
    'services.performance.desc': 'Data-driven paid advertising campaigns across all digital platforms',
    'services.growth.title': 'Growth Marketing',
    'services.growth.desc': 'Comprehensive growth strategies to expand your customer base',
    'services.ai-video.title': 'AI Video Production',
    'services.ai-video.desc': 'Creative video content using cutting-edge AI technology',
    
    // Stats
    'stats.visitors': 'Today\'s Visitors',
    'stats.conversion': 'Conversion Rate',
    'stats.speed': 'Website Speed',
    'stats.seo': 'SEO Score',
    'stats.clients': 'Happy Clients',
    'stats.views': 'Monthly Views',
    'stats.support': 'Support',
    'stats.roas': 'Average ROAS Increase',
    
    // Why Us
    'why-us.title': 'Why Choose Reesha?',
    'why-us.subtitle': 'We don\'t just provide services, we build success partnerships',
    'why-us.expert-team': 'Expert specialized team',
    'why-us.measurable-results': 'Measurable results',
    'why-us.advanced-tech': 'Advanced technologies',
    'why-us.support': '24/7 support',
    'why-us.testimonial': 'Reesha completely transformed our business trajectory. The results speak for themselves.',
    'why-us.rating': 'from clients',
    
    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': 'We\'re here to help you achieve your goals',
    'contact.phone': 'Phone',
    'contact.email': 'Email',
    'contact.address': 'Address',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.message': 'Message',
    'contact.form.send': 'Send Message',
    
    // Footer
    'footer.description': 'Data-driven growth partner',
    'footer.contact-us': 'Contact Us',
    'footer.follow-us': 'Follow Us',
    'footer.rights': 'All rights reserved',
    
    // Dashboard
    'dashboard.welcome': 'Welcome to Advanced Dashboard',
    'dashboard.welcome.subtitle': 'Manage your website easily and effectively with advanced tools and user-friendly interface',
    'dashboard.quick-setup': 'Quick Setup',
    'dashboard.guided-tour': 'Guided Tour',
    'dashboard.quick-actions': 'Quick Actions',
    'dashboard.recent-activity': 'Recent Activity',
    'dashboard.view-all': 'View All',
    'dashboard.processing': 'Processing...',
    
    // Quick Actions
    'actions.preview-website': 'Preview Website',
    'actions.publish-changes': 'Publish Changes',
    'actions.create-backup': 'Create Backup',
    'actions.download-theme': 'Download Theme',
    'actions.analytics-report': 'Analytics Report',
    'actions.optimize-performance': 'Optimize Performance',
    
    // Projects
    'projects.title': 'Project Management',
    'projects.new-project': 'New Project',
    'projects.status': 'Status',
    'projects.last-modified': 'Last Modified',
    'projects.views': 'Views',
    'projects.published': 'Published',
    
    // Tabs
    'tabs.dashboard': 'Dashboard',
    'tabs.projects': 'Projects',
    'tabs.visual-editor': 'Visual Editor',
    'tabs.content-manager': 'Content Manager',
    'tabs.design-system': 'Design System',
    'tabs.analytics': 'Analytics',
    'tabs.media-library': 'Media Library',
    'tabs.settings': 'Settings',
    
    // Coming Soon
    'coming-soon.title': 'Coming Soon',
    'coming-soon.subtitle': 'This section is under development and will be available soon',
    
    // Theme Download
    'download.title': 'WordPress Theme Download',
    'download.subtitle': 'Complete WordPress Theme Package',
    'download.features': 'Visual Editor • Multilingual Support • Admin Dashboard • OT Compatible',
    'download.ready': 'Theme Ready for Download!',
    'download.complete': 'Your complete WordPress theme package is ready',
    'download.button': 'Download Theme Info & Instructions',
    'download.location': 'Theme Files Location',
    'download.instructions': 'Installation Instructions',
    'download.requirements': 'Requirements',
    
    // Success Messages
    'success.saved': 'Saved successfully!',
    'success.published': 'Published successfully!',
    'success.deleted': 'Deleted successfully!',
    'success.updated': 'Updated successfully!',
    'success.created': 'Created successfully!',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<'ar' | 'en'>(() => {
    // Check localStorage first, then browser language, default to Arabic
    const saved = localStorage.getItem('reesha-language');
    if (saved && (saved === 'ar' || saved === 'en')) {
      return saved as 'ar' | 'en';
    }
    
    // Check browser language
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('ar')) {
      return 'ar';
    }
    
    return 'ar'; // Default to Arabic
  });

  const isRTL = language === 'ar';

  // Save language preference and update document
  useEffect(() => {
    localStorage.setItem('reesha-language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    
    // Update body class for styling
    document.body.classList.toggle('rtl', isRTL);
    document.body.classList.toggle('ltr', !isRTL);
  }, [language, isRTL]);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const handleLanguageChange = (newLanguage: 'ar' | 'en') => {
    setLanguage(newLanguage);
  };

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage: handleLanguageChange,
      t,
      isRTL
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;